<template>
  <div class="entry-table">
    <div class="entry-table__body">
      <el-table
        ref="table"
        :data="rows"
        :row-class-name="rowClassName"
        :empty-text="emptyText"
        height="100%"
        size="small"
      >
        <el-table-column type="index" label="№" width="44"></el-table-column>
        <el-table-column
          label="Дата"
          width="120"
          class-name="entry-table__date"
        >
          <template #default="{ row }">
            {{ row.label }}
            <span class="entry-table__weekday">{{ row.weekday }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Количество"
          :width="amountsLocked ? 120 : 150"
          :class-name="available ? 'entry-table__stock-col' : ''"
        >
          <template #header>
            <span class="entry-table__head">
              <span class="entry-table__head-label">Количество</span>
              <help-tip
                v-if="!amountsLocked || available"
                title="Количество"
                :content="amountTip"
              ></help-tip>
            </span>
          </template>
          <template #default="{ row, $index }">
            <!--
              Остаток - не отдельной колонкой: шкала - полоской по нижнему краю поля,
              мелкая подпись - под полем (отступы ячейки уменьшены), подробности - в подсказке
            -->
            <div
              class="entry-table__stock"
              :class="hasStock(row) && 'is-' + stock(row).level"
              :title="hasStock(row) ? stock(row).title : ''"
            >
              <span v-if="amountsLocked" class="entry-table__amount">
                {{ format(amounts[row.date]) }}
                <span class="entry-table__muted">{{ unit }}</span>
              </span>
              <el-input
                v-else
                :ref="'amount-' + row.date"
                :value="amountDisplay(row)"
                :class="{
                  'is-suggested': isSuggested(row),
                  'is-error': isOverAvailable(row),
                }"
                placeholder="Количество"
                size="small"
                @focus="startEdit(row)"
                @input="editText = $event"
                @blur="commit(row)"
                @keydown.enter.native.prevent="focusNext($index, $event)"
                @keydown.esc.native="cancelEdit($event)"
              ></el-input>
              <div v-if="hasStock(row)" class="entry-table__gauge">
                <div
                  class="entry-table__gauge-fill"
                  :style="{ width: stock(row).percent + '%' }"
                ></div>
              </div>
            </div>
            <div
              v-if="hasStock(row)"
              class="entry-table__stock-text"
              :class="'is-' + stock(row).level"
              :title="stock(row).title"
            >
              {{ stock(row).text }}
            </div>
          </template>
        </el-table-column>
        <el-table-column v-if="!amountsLocked" label="Ед. изм." width="80">
          <span class="entry-table__muted">{{ unit }}</span>
        </el-table-column>

        <el-table-column
          v-for="{ head, fields: columnFields } in columns"
          :key="head.column || head.key"
          :label="head.columnLabel || head.label"
          :min-width="head.minWidth || head.width"
        >
          <template #header>
            <span class="entry-table__head">
              <span
                class="entry-table__head-label"
                :title="head.columnLabel || head.label"
              >
                {{ head.columnLabel || head.shortLabel || head.label }}
              </span>
              <help-tip
                v-if="fieldTip(head)"
                :title="head.columnLabel || head.label"
                :content="fieldTip(head)"
              ></help-tip>
            </span>
          </template>
          <template #default="{ row }">
            <template v-for="field in shownFields(columnFields, row)">
              <el-select
                v-if="field.type === 'select'"
                :key="field.key"
                :value="row.values[field.key]"
                :disabled="isDisabled(row, field)"
                :class="{
                  'is-error': hasError(row, field),
                  'is-suggested': isDefault(row, field),
                }"
                value-key="id"
                placeholder="Выберите"
                size="small"
                filterable
                clearable
                @visible-change="onSelectVisible(row.date + field.key, $event)"
                @change="setField(row.date, field, $event)"
              >
                <el-option
                  v-for="option in visibleOptions(row, field)"
                  :key="option.id"
                  :label="option.name"
                  :value="option"
                  @click.native="confirmSame(row, field, option)"
                ></el-option>
              </el-select>
              <el-checkbox
                v-else-if="field.type === 'checkbox'"
                :key="field.key"
                :value="!!row.values[field.key]"
                :disabled="isDisabled(row, field)"
                @change="setField(row.date, field, $event)"
              ></el-checkbox>
              <el-input
                v-else-if="field.type === 'number' || field.type === 'text'"
                :key="field.key"
                :value="textDisplay(row, field)"
                :disabled="isDisabled(row, field)"
                :class="{
                  'is-error': hasError(row, field),
                  'is-suggested': isTextSuggested(row, field),
                }"
                :placeholder="field.type === 'number' ? '0' : field.label"
                size="small"
                @focus="textEdit = row.date + field.key"
                @input="setField(row.date, field, $event)"
                @blur="endTextEdit(row.date, field)"
              ></el-input>
              <template v-else-if="field.type === 'passport'">
                <el-tag
                  v-if="row.values[field.key]"
                  :key="field.key + '-tag'"
                  :data-field="field.key"
                  size="small"
                  closable
                  @close="setField(row.date, field, null)"
                >
                  Паспорт заполнен
                </el-tag>
                <el-button
                  v-else
                  :key="field.key + '-button'"
                  :data-field="field.key"
                  size="mini"
                  :disabled="isDisabled(row, field)"
                  @click="$emit('passport', row.date)"
                >
                  Заполнить
                </el-button>
              </template>
            </template>
          </template>
        </el-table-column>

        <el-table-column v-if="removable" width="60" align="center">
          <template #default="{ row }">
            <el-button
              type="text"
              icon="el-icon-delete"
              class="entry-table__delete"
              title="Удалить движение"
              @click="remove(row.date)"
            ></el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import HelpTip from "@/components/ecologist/dataEntryNew/helpTip";
import { NUMBER, isEmpty, rowStatus } from "./entryModes";

const WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

export default {
  name: "entryTable",
  components: { HelpTip },
  props: {
    // Выбранные даты YYYY-MM-DD по возрастанию (.sync - удаление строки)
    dates: { type: Array, required: true },
    // Внесённые количества { [date]: "0.123" } (.sync), без предложенных серых
    amounts: { type: Object, required: true },
    amountsLocked: { type: Boolean, default: false },
    unit: { type: String, default: "" },
    // Доступный остаток на дату { [date]: number } - для расходных режимов
    available: { type: Object, default: null },
    // Описание колонок, см. fieldsFor в entryModes.js
    fields: { type: Array, default: () => [] },
    // Значения полей { [date]: { [key]: value, _touched: { [key]: true } } } (.sync)
    values: { type: Object, default: () => ({}) },
    // Пустое основное поле - дату не проводить
    skippable: { type: Boolean, default: false },
    removable: { type: Boolean, default: true },
    showErrors: { type: Boolean, default: false },
    emptyText: { type: String, default: "Нет данных" },
  },
  data() {
    return {
      // Строка, количество которой сейчас редактируют, и введённый текст
      editDate: null,
      editText: "",
      // Открытый список: остальные выпадающие списки рисуют только выбранное значение,
      // иначе сотни строк × сотни вариантов подвешивают страницу
      openSelect: null,
      // Строка, к которой прокрутили из-за ошибки, - коротко подсвечивается
      flashDate: null,
      // Текстовое поле в фокусе (date + key): подставленное серое значение в нём не показываем
      textEdit: null,
    };
  },

  computed: {
    rows() {
      return this.dates.map((date) => {
        const [y, m, d] = date.split("-");
        return {
          date,
          label: `${d}.${m}.${y}`,
          weekday: WEEKDAYS[new Date(y, m - 1, d).getDay()],
          values: this.values[date] || {},
        };
      });
    },
    // Предлагаемое (серое) количество - первое внесённое по порядку дат
    suggestion() {
      const date = this.dates.find((d) => this.amounts[d] !== undefined);
      return date ? this.amounts[date] : null;
    },
    amountTip() {
      const tips = [];
      if (!this.amountsLocked)
        tips.push(
          "Введите количество отходов для первой даты и нажмите Enter — значение автоматически применится ко всем строкам. При необходимости измените значение в любой строке."
        );
      if (this.available)
        tips.push(
          "«Доступно» под полем — остаток отхода на дату за вычетом количеств в строках выше: каждая строка уменьшает остаток на все следующие даты."
        );
      return tips.join(" ");
    },
    // Колонки таблицы: поля с одинаковым column - в одной колонке, заголовок и ширина - первого
    columns() {
      const columns = [];
      this.fields.forEach((field) => {
        const column =
          field.column && columns.find((c) => c.head.column === field.column);
        if (column) column.fields.push(field);
        else columns.push({ head: field, fields: [field] });
      });
      return columns;
    },
    statuses() {
      const statuses = {};
      this.rows.forEach((row) => {
        statuses[row.date] = rowStatus(this.fields, row.values, this.skippable);
      });
      return statuses;
    },
  },

  watch: {
    // Новым строкам (например, добавленным датам) подставляем значения первой строки
    dates: {
      immediate: true,
      handler() {
        if (!this.fields.length) return;
        const missing = this.dates.filter((date) => !this.values[date]);
        if (!missing.length) return;
        const next = { ...this.values };
        const first = this.dates.find((date) => this.values[date]);
        missing.forEach((date) => {
          const row = {};
          if (first)
            this.fields
              .filter((field) => field.cascade)
              .forEach((field) => (row[field.key] = this.values[first][field.key]));
          next[date] = row;
        });
        this.$emit("update:values", next);
      },
    },
  },

  methods: {
    format(amount) {
      return amount === null || amount === undefined
        ? ""
        : String(amount).replace(".", ",");
    },

    roundAmount(value) {
      return value === undefined || value === null
        ? null
        : Math.round(value * 100000) / 100000;
    },

    rowClassName({ row }) {
      const classes = [];
      if (this.statuses[row.date].skip) classes.push("is-skipped");
      if (row.date === this.flashDate) classes.push("is-flash");
      return classes.join(" ");
    },

    /*
      Прокрутить к первой строке с ошибкой и подсветить её.
      Ошибка в количестве (нет ни одного или больше остатка) - ещё и курсор в поле количества
    */
    async scrollToFirstError() {
      let index = this.rows.findIndex((row) => this.isOverAvailable(row));
      let focusAmount = index >= 0;
      if (!this.amountsLocked && this.suggestion === null) {
        index = 0;
        focusAmount = true;
      }
      if (index < 0)
        index = this.rows.findIndex(
          (row) => !this.statuses[row.date].valid
        );
      if (index < 0) return;

      const date = this.rows[index].date;
      // По горизонтали - к ячейке с ошибкой (поле количества или первое выделенное поле),
      // иначе широкая таблица оставляет её за правым краем
      await this.scrollToRow(
        index,
        focusAmount ? ".is-suggested, .is-error, input" : ".is-error"
      );

      if (focusAmount) {
        const ref = this.$refs["amount-" + date];
        const input = Array.isArray(ref) ? ref[0] : ref;
        if (input) input.focus();
      }
    },

    // Прокрутить к полю key в строке даты и подсветить строку
    scrollToField(date, key) {
      const index = this.rows.findIndex((row) => row.date === date);
      if (index >= 0) return this.scrollToRow(index, `[data-field="${key}"]`);
    },

    // Строку - на середину по высоте, ячейку с selector - на середину по ширине
    async scrollToRow(index, selector) {
      const date = this.rows[index].date;
      await this.$nextTick();
      const wrapper = this.$refs.table.$el.querySelector(
        ".el-table__body-wrapper"
      );
      const tr = wrapper.querySelectorAll(".el-table__row")[index];
      if (tr) {
        const target = tr.querySelector(selector);
        const td = target && target.closest("td");
        const left = td
          ? td.offsetLeft - (wrapper.clientWidth - td.offsetWidth) / 2
          : wrapper.scrollLeft;
        wrapper.scrollTo({
          top: tr.offsetTop - (wrapper.clientHeight - tr.offsetHeight) / 2,
          left: Math.max(0, left),
          behavior: "smooth",
        });
      }

      this.flashDate = null;
      await this.$nextTick();
      this.flashDate = date;
      setTimeout(() => {
        if (this.flashDate === date) this.flashDate = null;
      }, 1600);
    },

    /*
      Шкала остатка под количеством: сколько доступного съедает строка.
      Пока поле в фокусе - по набираемому тексту, чтобы шкала двигалась при вводе
    */
    stock(row) {
      // Остатка на дату ещё нет (проверка с сервера не пришла или дата с проблемой)
      if (this.available[row.date] === undefined)
        return {
          total: null,
          percent: 0,
          level: "wait",
          text: "остаток проверяется…",
          title: "Остаток на дату ещё не получен с сервера",
        };
      const total = this.roundAmount(this.available[row.date]);
      const text =
        row.date === this.editDate ? this.editText : this.effectiveAmount(row.date);
      const amount = parseFloat(String(text || "").replace(",", ".")) || 0;
      const left = this.roundAmount(total - amount);
      const over = amount > total + 0.001;
      const percent =
        total > 0 ? Math.min(100, Math.round((amount / total) * 100)) : over ? 100 : 0;
      let level = "ok";
      if (over) level = "over";
      else if (percent >= 70) level = "warn";
      const unit = this.unit ? " " + this.unit : "";
      return {
        total,
        percent,
        level,
        text: over
          ? `сверх ${this.format(this.roundAmount(-left))}${unit}`
          : amount > 0
          ? `останется ${this.format(left)}${unit}`
          : `доступно ${this.format(total)}${unit}`,
        title: over
          ? `Превышение на ${this.format(this.roundAmount(-left))}${unit} (доступно ${this.format(total)}${unit})`
          : amount > 0
          ? `Доступно ${this.format(total)}${unit}, останется ${this.format(left)}${unit} (${percent}% использовано)`
          : `Доступно ${this.format(total)}${unit}`,
      };
    },

    // Шкала и подпись - во всех строках режимов расхода, даже пока остаток не получен
    hasStock() {
      return !!this.available;
    },

    // Поля колонки, видимые в строке (у объединённой колонки - одно)
    shownFields(fields, row) {
      return fields.filter((field) => !field.hidden || !field.hidden(row.values));
    },

    // ---- Количество ----

    effectiveAmount(date) {
      const own = this.amounts[date];
      return own !== undefined ? own : this.suggestion;
    },

    isSuggested(row) {
      return (
        row.date !== this.editDate && this.amounts[row.date] === undefined
      );
    },

    isOverAvailable(row) {
      if (!this.available || this.available[row.date] === undefined)
        return false;
      const amount = parseFloat(this.effectiveAmount(row.date));
      return amount > this.available[row.date] + 0.001;
    },

    amountDisplay(row) {
      if (row.date === this.editDate) return this.editText;
      return this.format(this.effectiveAmount(row.date));
    },

    // При начале правки предложенное серое значение исчезает
    startEdit(row) {
      this.editDate = row.date;
      this.editText = this.format(this.amounts[row.date]);
    },

    cancelEdit(event) {
      this.editDate = null;
      event.target.blur();
    },

    // Уход с поля (в том числе по Enter): число становится внесённым, пустое - снова предлагаемым
    commit(row) {
      if (this.editDate !== row.date) return;
      const text = this.editText.trim();
      this.editDate = null;

      const next = { ...this.amounts };
      if (!text) delete next[row.date];
      else if (NUMBER.test(text)) next[row.date] = text.replace(",", ".");
      else {
        this.$message.warning("Количество должно быть числом, например 0,125");
        return;
      }
      this.$emit("update:amounts", next);
    },

    // Enter переводит фокус на следующую строку, на последней - завершает ввод
    focusNext(index, event) {
      const date = this.dates[index + 1];
      if (!date) return event.target.blur();
      const ref = this.$refs["amount-" + date];
      (Array.isArray(ref) ? ref[0] : ref).focus();
    },

    // ---- Поля режима ----

    fieldTip(field) {
      if (field.tip === "" || (!field.cascade && !field.tip)) return "";
      let tip =
        field.tip ||
        (field.type === "checkbox"
          ? "Поставьте отметку на первой дате — этот выбор автоматически применится ко всем строкам. При необходимости снимите отметку в любой строке."
          : "Выберите значение для первой даты — оно автоматически применится ко всем строкам. При необходимости измените значение в любой строке.");
      if (this.skippable && field.primary)
        tip +=
          " Очистите поле, чтобы не проводить дату - отход останется на временном хранении.";
      return tip;
    },

    // el-select не шлёт change при выборе того же значения - а это тоже ручной выбор:
    // подставленное серое значение становится чёрным
    confirmSame(row, field, option) {
      const value = row.values[field.key];
      if (!value || value.id !== option.id || !this.isDefault(row, field)) return;
      this.$emit("update:values", {
        ...this.values,
        [row.date]: {
          ...row.values,
          _touched: { ...row.values._touched, [field.key]: true },
        },
      });
    },

    // Подставленное значение (из первой строки или автоматически), которое в строке не выбирали
    isDefault(row, field) {
      const touched = row.values._touched || {};
      return !isEmpty(row.values[field.key]) && !touched[field.key];
    },

    // Подставленное из первой строки текстовое значение: серое, при нажатии на поле исчезает
    isTextSuggested(row, field) {
      return (
        !!field.cascade &&
        this.textEdit !== row.date + field.key &&
        this.isDefault(row, field)
      );
    },

    textDisplay(row, field) {
      const editing = this.textEdit === row.date + field.key;
      if (editing && field.cascade && this.isDefault(row, field)) return "";
      return row.values[field.key];
    },

    // Ушли с поля, ничего не вписав, - снова подставляется значение первой строки
    endTextEdit(date, field) {
      this.textEdit = null;
      const first = this.dates[0];
      const values = this.values[date] || {};
      const suggested = (this.values[first] || {})[field.key];
      if (!field.cascade || date === first || !isEmpty(values[field.key])) return;
      if (isEmpty(suggested) || (field.disabled && field.disabled(values))) return;
      const next = { ...this.values };
      this.applyField(next, date, field, suggested, false);
      this.$emit("update:values", next);
    },

    isDisabled(row, field) {
      return field.disabled ? field.disabled(row.values) : false;
    },

    hasError(row, field) {
      return this.showErrors && !!this.statuses[row.date].errors[field.key];
    },

    optionsFor(field, values, date) {
      return typeof field.options === "function"
        ? field.options(values, date)
        : field.options || [];
    },

    // При переходе из одного списка в другой «открылся новый» приходит раньше «закрылся старый»:
    // закрытие сбрасывает только свой список, иначе только что открытый урезается до одного варианта
    onSelectVisible(key, visible) {
      if (visible) this.openSelect = key;
      else if (this.openSelect === key) this.openSelect = null;
    },

    visibleOptions(row, field) {
      if (this.openSelect === row.date + field.key)
        return this.optionsFor(field, row.values, row.date);
      const value = row.values[field.key];
      return value ? [value] : [];
    },

    // Значение и зависимые поля одной строки. Зависимые поля становятся подставленными
    applyField(next, date, field, value, touch) {
      const row = { ...next[date], [field.key]: value };
      const patch = field.onChange ? field.onChange(value, row) : {};
      Object.assign(row, patch);
      const touched = { ...row._touched };
      Object.keys(patch).forEach((key) => delete touched[key]);
      if (touch) touched[field.key] = true;
      else delete touched[field.key];
      row._touched = touched;
      next[date] = row;
    },

    fits(field, date, values, value) {
      if (field.type !== "select" || typeof field.options !== "function")
        return true;
      return this.optionsFor(field, values, date).some(
        (option) => option.id === value.id
      );
    },

    /*
      Значение первой строки подставляется во все строки, где это поле не выбирали вручную.
      Ручной выбор помечает поле строки как тронутое - туда больше не подставляем.
      Очистка в первой строке на остальные не распространяется.
    */
    setField(date, field, value) {
      if (value === "" && field.type === "select") value = null;
      const next = { ...this.values };
      const isFirst = date === this.dates[0];
      this.applyField(next, date, field, value, true);

      if (isFirst && field.cascade && !isEmpty(value)) {
        this.dates.slice(1).forEach((other) => {
          const values = next[other] || {};
          if (values._touched && values._touched[field.key]) return;
          // В недоступное поле строки не подставляем (например, контрагента при физ. лице)
          if (field.disabled && field.disabled(values)) return;
          if (!this.fits(field, other, values, value)) return;
          this.applyField(next, other, field, value, false);
        });
      }
      this.$emit("update:values", next);
    },

    remove(date) {
      const amounts = { ...this.amounts };
      delete amounts[date];
      this.$emit("update:amounts", amounts);
      this.$emit(
        "update:dates",
        this.dates.filter((d) => d !== date)
      );
    },
  },
};
</script>

<style scoped>
.entry-table {
  display: flex;
  flex-direction: column;
  height: 100%;
}
/* Название и «?» в одну строку: длинное название обрезается, значок остаётся */
/* Шапка всех колонок - как заголовки групп карточек («Поступление», «Расход») */
.entry-table ::v-deep .el-table th > .cell {
  font-size: 13px;
  font-weight: 600;
  color: #909399;
}
.entry-table__head {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  white-space: nowrap;
  vertical-align: middle;
}
.entry-table__head-label {
  overflow: hidden;
  text-overflow: ellipsis;
}
.entry-table__body {
  flex: 1;
  min-height: 0;
}
/* Дата и день недели всегда в одну строку - колонка фиксированной ширины под них */
.entry-table ::v-deep .entry-table__date .cell {
  white-space: nowrap;
}
.entry-table__weekday {
  margin-left: 4px;
  color: #909399;
}
.entry-table__amount {
  color: #303133;
}
.entry-table__muted {
  color: #606266;
}
/*
  Шкала остатка: зелёная → оранжевая (от 70%) → красная полосатая при превышении.
  Полоска лежит по нижнему краю поля внутри рамки (absolute): высота строки не меняется,
  а .cell у el-table обрезает всё, что выходит за неё
*/
.entry-table__stock {
  --gauge: #67c23a;
  position: relative;
}
.entry-table__stock.is-warn {
  --gauge: #e6a23c;
}
.entry-table__stock.is-over {
  --gauge: #f56c6c;
  animation: entry-table-shake 0.4s ease-in-out;
}
/*
  Подпись под полем: строка 12px, а отступы ячейки срезаны с 8 до 3/2px -
  строка таблицы выше обычной всего на 1px (3 + 32 + 12 + 2 против 8 + 32 + 8)
*/
.entry-table ::v-deep td.entry-table__stock-col {
  padding: 3px 0 2px;
}
.entry-table__stock-text {
  margin-top: 0;
  font-size: 10.5px;
  line-height: 12px;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.entry-table__stock-text.is-warn {
  color: #e6a23c;
}
.entry-table__stock-text.is-over {
  color: #f56c6c;
  font-weight: bold;
}
.entry-table__gauge {
  position: absolute;
  left: 4px;
  right: 4px;
  bottom: 1px;
  height: 3px;
  border-radius: 2px;
  background: rgba(235, 238, 245, 0.8);
  overflow: hidden;
  pointer-events: none;
}
.entry-table__gauge-fill {
  height: 100%;
  border-radius: inherit;
  background: var(--gauge);
  box-shadow: 0 0 6px var(--gauge);
  transition: width 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
    background-color 0.25s;
}
.is-over .entry-table__gauge-fill {
  background-image: repeating-linear-gradient(
    -45deg,
    rgba(255, 255, 255, 0.45) 0 4px,
    transparent 4px 8px
  );
  background-size: 11px 11px;
  animation: entry-table-stripes 0.6s linear infinite;
}
@keyframes entry-table-stripes {
  to {
    background-position: 11px 0;
  }
}
@keyframes entry-table-shake {
  20%,
  60% {
    transform: translateX(-3px);
  }
  40%,
  80% {
    transform: translateX(3px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .entry-table__stock.is-over,
  .is-over .entry-table__gauge-fill {
    animation: none;
  }
}

.el-select,
.el-input {
  width: 100%;
}
.el-input ::v-deep .el-input__inner {
  color: #303133;
}
.is-suggested ::v-deep .el-input__inner {
  color: #c0c4cc;
}
.is-error ::v-deep .el-input__inner {
  border-color: #f56c6c;
}

/* Строка, к которой прокрутили из-за ошибки */
@keyframes entry-row-flash {
  from {
    background: #fde2e2;
  }
  to {
    background: transparent;
  }
}
.entry-table ::v-deep .el-table__row.is-flash td {
  animation: entry-row-flash 1.6s ease-out;
}

/* Дату не проводим - строка приглушена */
.entry-table ::v-deep .el-table__row.is-skipped td {
  background: #fafafa;
}
.entry-table ::v-deep .el-table__row.is-skipped .entry-table__amount {
  color: #c0c4cc;
}

.entry-table__delete {
  padding: 6px;
  border-radius: 50%;
  color: #c0c4cc;
  transition: color 0.15s, background-color 0.15s;
}
.entry-table__delete:hover,
.entry-table__delete:focus {
  color: #f56c6c;
  background: #fef0f0;
}
</style>
